// Signing in, and getting a token the API will accept.
//
// The API refuses an application-only token: the registration exposes one
// delegated scope and no application permissions, so every token here is issued
// to the signed-in person and the audit columns record their sign-in name.
//
// ANYBODY IN THE TENANT CAN SIGN IN, and a token carrying no app role is the
// ordinary case rather than a fault. Whether that person may use the
// application is the API's to say, from a grant an Admin made in its own table,
// and `/api/me` says it; `shell/AccessGate.tsx` shows the landing page to
// somebody who may not.

import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { config } from './config';

let msal: PublicClientApplication | null = null;

export async function initAuth(): Promise<PublicClientApplication> {
  if (msal) return msal;
  const { tenantId, apiClientId } = config();
  msal = new PublicClientApplication({
    auth: {
      // The browser client signs in against the same registration the API
      // validates tokens for, which is why one client id serves both. It is
      // declared as a single-page application, so the code comes back through
      // the authorization code flow with PKCE and never through an implicit
      // grant -- which is what lets a browser holding no secret complete a
      // sign-in.
      clientId: apiClientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      // THE TRAILING SLASH IS LOAD-BEARING. The registration declares
      // `https://<client web app>.azurewebsites.net/` -- the provider refuses
      // the form without it at plan time -- and Entra matches a redirect URI
      // as a string. `window.location.origin` carries no trailing slash, so
      // sending it unmodified produces `AADSTS50011`, which names the URI that
      // did not match and reads as a misconfigured registration.
      redirectUri: `${window.location.origin}/`,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // Session rather than local: a shared shop-floor browser should not keep
      // somebody signed in after the tab closes.
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  });
  await msal.initialize();
  await msal.handleRedirectPromise();
  return msal;
}

export function account(): AccountInfo | null {
  return msal?.getAllAccounts()[0] ?? null;
}

export async function signIn(): Promise<void> {
  const client = await initAuth();
  // Redirect rather than popup. A popup is blocked by default on some of the
  // devices this runs on, and the failure is silent.
  await client.loginRedirect({ scopes: [config().apiScope] });
}

export async function signOut(): Promise<void> {
  const client = await initAuth();
  await client.logoutRedirect({ account: account() ?? undefined });
}

/** A token for the API, silently where possible.
 *
 *  `acquireTokenSilent` covers the ordinary case; an
 *  `InteractionRequiredAuthError` means the refresh token expired or a
 *  conditional-access policy wants the person back, and the only answer to that
 *  is to send them to the authority. */
export async function apiToken(): Promise<string> {
  const client = await initAuth();
  const current = account();
  if (!current) {
    await signIn();
    throw new Error('redirecting to sign in');
  }
  const request = { scopes: [config().apiScope], account: current };
  try {
    const result: AuthenticationResult = await client.acquireTokenSilent(request);
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      await client.acquireTokenRedirect(request);
      throw new Error('redirecting to sign in');
    }
    throw error;
  }
}
